<template>
  <div>
    <div class="rev-header__bg">
      <div class="d-flex justify-space-between align-center">
        <div class="d-flex align-center justify-center">
          <RevStatus
            large
            showValidationIcons
            :isApproved="isApproved"
            :canEdit="permissions.editStatus"
            :rev="rev"
            @onChange="handleChangeStatus"
          />
          <RevActionIcons
            class="ps-4"
            :revType="revType"
            :isLiked="isLiked"
            :isSaving="isSaving"
            :canHighlight="permissions.canHighlight"
            :isHighlighted="revIsHighlighted"
            :likesCount="rev.totalLikes"
            @optionClicked="handleOptionClicked"
          />
        </div>
        <div class="flex-grow-1">
          <PlannerIntegrationBadge
            v-if="isPlannerBadgesEnabled"
            class="mr-3"
            :data="plannerIntegration"
            :tooltipMessage="$t('common.click_to_open_rev_in_ms_planner')"
          />
        </div>
        <div v-if="isEditMode">
          <Button
            v-if="!isSaving"
            plain
            class="me-2"
            color="white"
            data-testid="rev-header-cancel-button"
            data-tourid="rev-view-button-cancel"
            :text="$t('common.button.cancel')"
            @click="cancelRevSave"
          />
          <Button
            color="primary"
            data-testid="rev-header-save-button"
            data-tourid="rev-view-button-save"
            :loading="isSaving"
            :text="$t('common.button.save')"
            @click="saveRev"
          />
        </div>
        <div v-else class="d-flex align-center">
          <RevMenuOptions
            class="mt-1"
            :likeTid="likeTid"
            :likedTid="likedTid"
            :iconOptions="iconOptions"
            :menuOptions="menuOptions"
            :isTranslated="isTranslated"
            :isTranslationEnabled="isTranslationEnabled"
            :isLiked="isLiked"
            :rev="rev"
            @optionClicked="handleOptionClicked"
          />
        </div>
      </div>
      <div>
        <div class="d-flex my-3 align-center flex-wrap">
          <p class="overline white--text mb-0">
            {{ revTypeText }}
          </p>
          <p v-if="isPointsEnabled" class="caption white--text mb-0 mx-1">-</p>
          <p v-if="isPointsEnabled" class="caption white--text mb-0">
            {{ $t('common.pointWithCount', { count: rev.points }) }}
          </p>
        </div>
        <input
          v-if="isEditMode"
          v-model="name"
          data-testid="revHeader-input-name"
          class="rev-header__input mb-3"
          :maxlength="140"
          @change="trackEvent('name')"
        />
        <p
          v-else-if="!isEditMode && !isTranslating"
          class="mb-3 rev-header__name"
        >
          <v-tooltip v-if="isReplica" top max-width="200px">
            <template v-slot:activator="{ on }">
              <v-chip
                v-on="on"
                class="me-1"
                color="white"
                outlined
                small
                @click="goToOriginalRev"
              >
                <v-icon x-small left> mdi-content-duplicate </v-icon>
                {{ $t('rev.replication.replica_label') }}
              </v-chip>
            </template>

            {{ $t('rev.replication.replica_tooltip') }}
          </v-tooltip>

          {{ name }}
        </p>
        <v-skeleton-loader
          v-else-if="isTranslating"
          type="image"
          class="mb-5"
          width="400px"
          height="30px"
        />

        <Team
          :rev="rev"
          :availableCoaches="availableCoaches"
          :availableOwners="availableOwners"
          :availableCollaborators="availableCollaborators"
          :organizationId="organizationId"
          :siteId="accountSiteId"
          :canEdit="isEditMode"
          @coachSelected="coachSelected"
          @ownerSelected="ownerSelected"
          @collaboratorsSelected="collaboratorsSelected"
          @coachFilter="coachFilter"
          @ownerFilter="ownerFilter"
          @collaboratorsFilter="collaboratorsFilter"
        />
      </div>
    </div>
    <DeleteConfirmationModal
      :show="showDeleteModal"
      :isLoading="isDeleting"
      :title="$t('rev.delete_this_rev')"
      :text="$t('rev.are_you_sure_you_want_to_remove_permanently_this_rev')"
      @cancel="handleCancelDeletion"
      @delete="onDeleteRev"
    />
    <v-dialog v-model="showErrorModal" max-width="300">
      <v-card>
        <v-card-title>
          {{ $t('rev.errors.title') }}
        </v-card-title>
        <v-card-text>
          {{ $t('rev.errors.site_error') }}
        </v-card-text>
        <v-card-actions class="justify-end">
          <Button
            plain
            color="grey"
            :text="$t('common.button.close')"
            @click="showErrorModal = false"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <HistoryModal
      v-if="isHistoryModalShown"
      :show="isHistoryModalShown"
      :revId="revId"
      :revType="revType"
      @close="isHistoryModalShown = false"
    />
    <ChangedModalStatus
      v-if="showStatusModal"
      :status="revstatusChanged"
      :show="showStatusModal"
      :title="changedModalStatustext.title"
      :subtitle="changedModalStatustext.subtitle"
      :placeholder="$t('common.cancel_rev')"
      @updateModalStatus="handleChangeModalStatus($event)"
      @hideModal="$emit('hideModal')"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import _ from 'lodash'
import { IMAGE_SIZES } from 'rever-media-sdk'

import defaultImage from '@/assets/rev/rev-header-default.jpg'
import LANGUAGES_DICTIONARY from '@/locales/dictionary.json'

import Team from '@/components/Rev/Team.vue'
import RevStatus from '@/components/Rev/RevStatus.vue'
import RevMenuOptions from '@/components/Rev/RevMenuOptions.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import Button from '@/components/UI/Button'
import HistoryModal from './HistoryModal.vue'
import ChangedModalStatus from '@/containers/Modals/ChangedModalStatus.vue'
import RevActionIcons from './RevActionIcons.vue'
import PlannerIntegrationBadge from '@/components/PlannerIntegrationBadge.vue'

import mixin from '@/mixins/mixin'
import revMixin from '@/mixins/revMixin'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import revPermissionsMixin from '@/mixins/revPermissionsMixin'

export default {
  name: 'RevHeader',

  components: {
    RevMenuOptions,
    Team,
    RevStatus,
    DeleteConfirmationModal,
    HistoryModal,
    Button,
    ChangedModalStatus,
    RevActionIcons,
    PlannerIntegrationBadge,
  },

  props: {
    isEditMode: {
      type: Boolean,
      required: true,
      default() {
        return false
      },
    },
    organizationId: {
      type: String,
      required: true,
    },
    accountSiteId: {
      type: String,
      required: true,
    },
    accountId: {
      type: String,
      required: true,
    },
    rev: {
      type: Object,
      required: true,
    },
    mediaObject: {
      type: Object,
      required: false,
    },
    availableCoaches: {
      type: Array,
      required: false,
    },
    availableOwners: {
      type: Array,
      required: false,
    },
    availableCollaborators: {
      type: Array,
      required: false,
    },
    isLiked: {
      type: Boolean,
      required: false,
      default: false,
    },
    isApproved: {
      type: Boolean,
      required: false,
      default: false,
    },
    menuOptions: {
      type: Array,
      required: false,
      default() {
        return []
      },
    },
    likeTid: {
      type: String,
      required: false,
      default: 'like',
    },
    likedTid: {
      type: String,
      required: false,
      default: 'liked',
    },
    shareType: {
      type: String,
      required: false,
    },
    revType: {
      type: String,
      required: false,
    },
    deleteRev: {
      type: Function,
      required: false,
    },
    trackCancelDelete: {
      type: Function,
      required: false,
      default() {
        return true
      },
    },
    trackStartDelete: {
      type: Function,
      required: false,
      default() {
        return true
      },
    },
    trackUpdateRev: {
      type: Function,
      required: false,
      default() {
        return true
      },
    },
    trackPrintRev: {
      type: Function,
      required: false,
      default() {
        return true
      },
    },
    setCoach: {
      type: Function,
      required: false,
    },
    setOwner: {
      type: Function,
      required: false,
    },
    setCollaborators: {
      type: Function,
      required: false,
    },
    updateName: {
      type: Function,
      required: false,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
    canSave: {
      type: Boolean,
      default: true,
    },
    showStatusModal: {
      type: Boolean,
      default: false,
    },
    isTranslating: Boolean,
    revApprovals: Number,
    plannerIntegration: {
      type: Object,
      default: null,
    },
  },

  mixins: [mixin, revMixin, trackerMixin, revPermissionsMixin],

  data() {
    return {
      showQuickActionModal: false,
      defaultImage,
      IMAGE_SIZES,
      showDeleteModal: false,
      showErrorModal: false,
      isDeleting: false,
      isHistoryModalShown: false,
      revstatusChanged: '',
      showApprovalModal: false,
    }
  },

  computed: {
    ...mapGetters([
      'account',
      'language',
      'availableFeatures',
      'organization',
      'organizationSiteGroups',
    ]),

    permissions() {
      return this.getPermissions({
        rev: this.rev,
        account: this.account,
        isTranslated: this.isTranslated,
        organizationSiteGroups: this.organizationSiteGroups,
      })
    },

    isTranslated() {
      return Boolean(this.$route?.query?.isTranslated)
    },

    isPlannerBadgesEnabled() {
      return this.$isFeatureEnabled(this.$featuresKeys?.plannerBadges)
    },

    revIsHighlighted() {
      return this.isHighlighted(this.rev)
    },

    name: {
      get() {
        return this.rev.name
      },
      set(name) {
        this.$emit('revChanged')
        this.updateName(name)
      },
    },

    owner: {
      get() {
        return this.rev.team.owner
      },
      set(owner) {
        this.setOwner(owner)
      },
    },

    coach: {
      get() {
        return this.rev.team.coach
      },
      set(coach) {
        this.setCoach(coach)
      },
    },

    collaborators: {
      get() {
        return this.rev.collaborators
      },
      set(collaborators) {
        this.setCollaborators(collaborators)
      },
    },

    iconOptions() {
      return _.compact([
        this.permissions.edit && {
          id: 'edit',
          icon: 'mdi-pencil-outline',
          tid: `${this.revType}-button-edit`,
          label: this.$t('rev.header.edit_rev'),
        },
      ])
    },

    isTranslationEnabled() {
      const revLang = this.rev.lang
      if (
        !revLang ||
        revLang === this.language ||
        !LANGUAGES_DICTIONARY?.['primary-dialects'][revLang]
      )
        return false

      return this.availableFeatures.includes('translation')
    },

    revTypeText() {
      const customNames =
        this.organization?.customNamings[this.revType]?.singular ?? ''

      return customNames
        ? customNames
        : this.$t(`common.rev_type.${_.snakeCase(this.revType)}_one`)
    },

    revId() {
      return this.rev.id
    },

    isPointsEnabled() {
      return this.$isFeatureEnabled(this.$featuresKeys?.points)
    },

    isApprovedByMe() {
      return this.rev.approvedByMe
    },

    changedModalStatustext() {
      if (this.revstatusChanged == this.$constants.revStatus.canceled)
        return {
          title: this.$t('common.nice_try'),
          subtitle: this.$t('common.marked_rev_as_canceled'),
        }
      return {
        title: this.$t('common.need_a_break'),
        subtitle: this.$t('common.marked_rev_as_onHold'),
      }
    },

    linkToPlanner() {
      return `https://tasks.office.com/Home/Task/${this.plannerIntegration?.externalEntityId}`
    },

    isReplica() {
      return Boolean(this.rev?.replicatedFromRevId)
    },
  },

  methods: {
    ...mapActions({
      showModal: 'modals/showModal',
    }),

    redirectToPrint() {
      const route = this.$router.resolve({
        name: `${this.revType}Print`,
        params: {
          organizationId: this.organizationId,
          revId: this.revId,
        },
        query: { isTranslated: this.isTranslated },
      })
      window.open(route.href)
    },

    coachFilter(changedUser) {
      this.$emit('coachFilter', changedUser)
      this.trackEvent('searchCoach')
    },

    ownerFilter(changedUser) {
      this.$emit('ownerFilter', changedUser)
      this.trackEvent('searchOwner')
    },

    collaboratorsFilter(selectedCollaborators) {
      this.$emit('collaboratorsFilter', selectedCollaborators)
      this.trackEvent('searchTeamMember')
    },

    toggleTranslate() {
      this.trackTranslationButton()
      this.$emit('toggleTranslate', {
        rev: this.rev,
      })
    },

    saveRev() {
      if (!this.canSave) {
        this.showErrorModal = true
        return
      }
      this.$emit('save')
    },

    handleChangeStatus(status) {
      this.$emit('statusChanged', status)
      this.revstatusChanged = status
    },

    handleChangeModalStatus(status) {
      this.$emit('changeModalStatus', status)
    },

    cancelRevSave() {
      this.$emit('cancel')
    },

    coachSelected(coach) {
      if (coach) coach.coaching = coach.coaching + 1
      this.setCoach(coach)
      this.$emit('revChanged')
      this.trackEvent('coach')
    },

    ownerSelected(owner) {
      if (owner) owner.coaching = owner.coaching + 1
      this.setOwner(owner)
      this.$emit('revChanged')
      this.trackEvent('owner')
    },

    collaboratorsSelected(collaborators) {
      this.setCollaborators(collaborators)
      this.$emit('revChanged')
      this.trackEvent('teamMember')
    },

    handleOptionClicked(optionId) {
      switch (optionId) {
        case 'edit':
          return this.$emit('editRev')
        case 'translate':
          return this.toggleTranslate()
        case 'history':
          this.isHistoryModalShown = true
          break
        case 'approve':
          this.handleApproveRev(true)
          break
        case 'disapprove':
          this.handleApproveRev(false)
          break
        case 'implementRev':
          this.$emit('implement')
          break
        case 'validate':
          return this.$emit('validate')
        case 'highlightRev':
          return this.$emit('highlight')
        case 'like':
          this.$emit('like')
          break
        case 'unlike':
          this.$emit('unlike')
          break
        case 'share':
          this.showModal({
            type: 'shareModal',
            props: {
              shareType: this.shareType,
              title: this.$t('common.share.rev'),
              entityId: this.rev.id,
              organizationId: this.organizationId,
              name: this.name,
              sharingAccountId: this.accountId,
            },
          })
          break
        case 'print':
          this.redirectToPrint()
          return this.trackPrintRev()
        case 'powerpoint':
          this.$emit('downloadPowerpoint')
          break
        case 'replicate':
          this.$emit('replicateRev')
          break
        case 'delete':
          this.showDeleteModal = true
          this.trackStartDelete()
          break
        default:
          return
      }
    },

    goToChecklists() {
      this.navigateTo({
        name: 'revsChecklist',
        params: {
          revType: _.kebabCase(this.revType),
          organizationId: this.organizationId,
          revId: this.rev.id,
        },
      })
    },

    trackEvent(changedField) {
      this.trackUpdateRev(changedField)
    },

    hideModal() {
      this.showDeleteModal = false
    },

    handleCancelDeletion: function () {
      this.trackCancelDelete()
      this.hideModal()
    },

    onDeleteRev() {
      this.isDeleting = true
      this.deleteRev()
        .then(() => {
          this.isDeleting = false
          this.hideModal()
          this.showSuccess(
            this.$t('rev.your_idea_has_been_successfully_deleted')
          )
          this.navigateTo({ name: 'explore' })
        })
        .catch(() => {
          this.showError(
            this.$t(
              'error.500.something_bad_happened_we_probably_did_something_wrong'
            )
          )
          this.isDeleting = false
          this.hideModal()
        })
    },

    handleApproveRev() {
      if (this.isApprovedByMe) {
        this.$emit('disapprove')
      } else {
        this.$emit('approve')
      }
    },

    goToOriginalRev() {
      const link = this.generateRevRoute(
        this.organizationId,
        this.revType,
        this.rev.replicatedFromRevId
      )

      window.open(link, '_blank')
    },
  },
}
</script>

<style lang="scss" scoped>
.rev-header {
  border-radius: 4px 4px 0 0;

  &__input {
    width: 100%;
    padding: 8px;
    resize: none;
    background: rgba(#1a202c, 0.5);
    border: 1px solid #f7fafc;
    border-radius: 4px;
    color: $color-white;
    font-style: normal;
    font-weight: 600;
    font-size: 20px;
    line-height: 24px;
  }
  &__name {
    font-size: 22px;
    line-height: 24px;
    letter-spacing: 0.25px;
    color: $color-white;
    overflow-wrap: break-word;
  }
  &__cancel {
    margin-right: 12px;
  }
  &__bg {
    padding: 24px 24px 12px 24px;
    box-shadow: 0px 1px 2px rgba(120, 135, 155, 0.3);
    border-radius: 4px 4px 0 0;
    background: $color-blue-grey-darken3;
    background-size: cover !important;
    min-height: 230px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  &__gradient {
    padding: 24px 24px 12px 24px;
    box-shadow: 0px 1px 2px rgba(120, 135, 155, 0.3);
    border-radius: 4px 4px 0 0;
    background: linear-gradient(
      180deg,
      rgba($color-blue-grey-darken3, 0.3) 0%,
      rgba($color-blue-grey-darken3, 1) 100%
    );
    background-size: cover !important;
    min-height: 230px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .checklist-button {
    padding: 0 12px !important;
  }
}
</style>
